import React, { useEffect, useState, ReactElement, useRef } from 'react';
import { Drawer, Button, Select } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { ApplicationDetailModel } from '../../../store/models/applicationDetailModel';
import { ReactComponent as RemoveIconDragger } from '../../../assets/images/svg/RemoveIconDragger.svg';
import { ReactComponent as AddIconDragger } from '../../../assets/images/svg/AddIconDragger.svg';
import { ReactComponent as DraggerIcon } from '../../../assets/images/svg/DraggerIcon.svg';
import { ReactComponent as XLS_Icon } from '../../../assets/images/svg/XLS_Icon.svg';
import { ReactComponent as CSV_Icon } from '../../../assets/images/svg/CSV_Icon.svg';
import { ReactComponent as DownOutlined } from '../../../assets/images/svg/ReportingDownChevronUnselected.svg';
import {
  REPORTING_BUTTON_TEXT,
  REPORTING_COLUMN_CONFIG_MAP,
  REPORTING_CONSTANTS,
  REPORT_ROUTE_MAP,
} from './reportingAndAnalyticsConstants';
import { ColumnCustomizerDrawerProps } from '../../../shared/interfaces/reportingAndAnalyticsInterface';
import { isEmpty } from 'lodash';
import { useHistory } from 'react-router-dom';

const { Option } = Select;

const ColumnCustomizerDrawer: React.FC<ColumnCustomizerDrawerProps> = ({
  visible,
  onClose,
  onSave,
  handleDownloadClicked,
  setSelectedFileFormat,
}: ColumnCustomizerDrawerProps): ReactElement => {
  const [fileFormat, setFileFormat] = useState<string>(
    REPORTING_CONSTANTS.XLSX,
  );
  const [isChanged, setIsChanged] = useState(false);
  const appDetail = useSelector(
    (state: RootState): ApplicationDetailModel => state.applicationDetail,
  );
  const { location: { pathname } } = useHistory();
  const tabName = REPORT_ROUTE_MAP[pathname];
  const userDetails = appDetail?.getUserDetail();
  const ReportingConfig = userDetails?.properties?.[REPORTING_COLUMN_CONFIG_MAP[tabName]] ?? [];
  // Keep local copy
  const [columns, setColumns] = useState<any[]>([]);

  const initialListRef = useRef<any[]>([]);

  useEffect((): void => {
    if (visible) {
      setColumns(ReportingConfig || []);
      initialListRef.current = ReportingConfig || [];
    }
  }, [visible]); // only reset when drawer opens

  // Detect changes compared to initial ref
  useEffect((): void => {
    const original = JSON.stringify(initialListRef.current || []);
    const updated = JSON.stringify(columns || []);
    setIsChanged(original !== updated);
  }, [columns]);

  const handleSave = (): void => {
    onSave(columns);
    setIsChanged(false);
  };

  // Move column between visible and removed
  const toggleVisibility = (col: any): void => {
    setColumns((prev: any[]): any[] =>
      prev.map((column: any): any =>
        column.mappedComponent === col.mappedComponent
          ? { ...column, isVisible: !column.isVisible }
          : column,
      ),
    );
  };

  const { visibleCols, removedCols } = React.useMemo((): { visibleCols: typeof columns; removedCols: typeof columns } => {
    const visibleColsArray: Record<string, any>[] = [];
    const removedColsArray: Record<string, any>[] = [];

    columns?.forEach((col: Record<string, any>): void => {
      if (col.isVisible) { visibleColsArray.push(col); } else { removedColsArray.push(col); }
    });

    return { visibleCols: visibleColsArray, removedCols: removedColsArray };
  }, [columns]);

  // Drag reorder for visible columns
  const handleReorder = (fromIdx: number, toIdx: number): void => {
    const [item] = visibleCols.splice(fromIdx, 1);
    visibleCols.splice(toIdx, 0, item);
    setColumns([...visibleCols, ...removedCols]);
  };

  const fileFormatOptions = [
    {
      value: REPORTING_CONSTANTS.XLSX,
      icon: XLS_Icon,
      label: REPORTING_CONSTANTS.DOWNLOAD_AS_XLSX,
    },
    {
      value: REPORTING_CONSTANTS.CSV,
      icon: CSV_Icon,
      label: REPORTING_CONSTANTS.DOWNLOAD_AS_CSV,
    },
  ];

  return (
    <Drawer
      placement="right"
      width={400}
      onClose={onClose}
      visible={visible}
      title="Arrange Columns"
      className="custom-drawer"
    >
      <div className="drawer-scrollable-content">
        <div className="dragger-title">
          {REPORTING_CONSTANTS.DRAG_AND_REARRANGE_COLUMNS}
        </div>

        {/* Visible columns */}
        <div className="columns-list">
          {visibleCols?.map((col: Record<string, any>, idx: number): ReactElement => (
            <div
              key={col.mappedComponent}
              draggable = {true}
              className="column-item"
              onDragStart={(e: React.DragEvent<HTMLDivElement>): void =>
                e.dataTransfer.setData('colIndex', idx.toString())
              }
              onDrop={(e: React.DragEvent<HTMLDivElement>): void => {
                const fromIdx = parseInt(e.dataTransfer.getData('colIndex'), 10);
                handleReorder(fromIdx, idx);
              }}
              onDragOver={(e: React.DragEvent<HTMLDivElement>): void => e.preventDefault()}
            >
              <span className="drag-handle">
                <DraggerIcon />
              </span>
              <span className="column-text">{col.displayPropName}</span>
              <Button
                type="link"
                size="small"
                onClick={(): void => toggleVisibility(col)}
              >
                <RemoveIconDragger />
              </Button>
            </div>
          ))}
        </div>

        {/* Removed columns */}
        <div className="removed-columns">
          <div className="dragger-title">
            {REPORTING_CONSTANTS.REMOVED_COLUMNS}
          </div>
          {removedCols?.map((col: Record<string, any>): ReactElement => (
            <div key={col.mappedComponent} className="column-item">
              <span>{col.displayPropName}</span>
              <Button
                type="link"
                size="small"
                onClick={(): void => toggleVisibility(col)}
              >
                <AddIconDragger />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {isEmpty(visibleCols) && (
        <span className="response-msg failure">
          {REPORTING_CONSTANTS.SELECT_AT_LEAST_ONE_COLUMN}
        </span>
      )}

      {/* Footer */}
      <div className="drawer-fixed-footer">
        <div className="file-format">
          <label>{REPORTING_CONSTANTS.CHOOSE_FILE_FORMAT} </label>
          <Select
            value={fileFormat}
            onChange={(value: string): void => {
              setFileFormat(value);
              setSelectedFileFormat(value);
            }}
            suffixIcon={<DownOutlined />}
            getPopupContainer={(triggerNode: HTMLElement): HTMLElement =>
              triggerNode?.parentNode as HTMLElement
            }
          >
            {fileFormatOptions?.map(({ value, icon: Icon, label }: { value: string; icon: React.ElementType; label: string }):
             ReactElement => (
              <Option key={value} value={value}>
                <span className="select-option-content">
                  <Icon className="select-icon" />
                  {label}
                </span>
              </Option>
            ))}
          </Select>
        </div>
        <div className="footer-buttons">
          <Button
            onClick={handleSave}
            disabled={!isChanged || isEmpty(visibleCols)}
            className="save-btn"
          >
            {REPORTING_BUTTON_TEXT.SAVE}
          </Button>
          <Button
            onClick={handleDownloadClicked}
            className="download-btn mouse-pointer"
            disabled={isChanged}
          >
            {REPORTING_BUTTON_TEXT.DOWNLOAD}
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default ColumnCustomizerDrawer;
